import {
  X,
  Clock,
  AlertTriangle,
  CheckCircle,
  Activity,
} from "lucide-react";
import type { ChamadoDetalhes } from "../../../types/api";
import {
  ehNovaFuncionalidade,
  formatTempoRestante,
  getSeveridadeLabel,
} from "../../../utils/chamadoUtils";
import { PrioridadeEditor } from "./PrioridadeEditor";
import { useUpdatePrioridade } from "../hooks/useChamados";
import { useState } from "react";
import type { AnexoChamado } from "../../../types/portalCliente";
import { useAuthStore } from "../../../stores/authStore";
import { useAnexosChamado, useCapacidades, useDetalheChamado } from "../gestao/api";
import { Anexos, LinhaDoTempo } from "../gestao/ChamadoDetalhePage";
import { VisualizadorAnexo } from "../gestao/VisualizadorAnexo";
import { EtiquetaAcoesCliente, EtiquetaStatusEquipe } from "../../equipe/AcoesRapidasCartao";

interface ChamadoModalProps {
  isOpen: boolean;
  onClose: () => void;
  chamado: ChamadoDetalhes | null;
  isLoading?: boolean;
}

export const ChamadoModal = ({
  isOpen,
  onClose,
  chamado,
  isLoading = false,
}: ChamadoModalProps) => {
  const { mutate: updatePrioridade, isPending: isPrioridadeUpdating } =
    useUpdatePrioridade();

  // As operações completas da gestão, dentro da janela do quadro (05/10/2026): linha do tempo com
  // resposta e anexos, ações do cliente ou status da equipe, e a lista de anexos com o visualizador.
  // z-40 na janela: o visualizador e o diálogo de ação (z-50, no body) abrem por cima dela.
  const equipe = useAuthStore((state) => Boolean(state.user?.equipe));
  const escreve = Boolean(useCapacidades().data?.escritasHabilitadas);
  const idChamado = chamado?.id ?? 0;
  const detalhe = useDetalheChamado(idChamado, isOpen && Boolean(chamado));
  const anexos = useAnexosChamado(idChamado);
  const [visualizando, setVisualizando] = useState<number | null>(null);
  const exibiveis = (anexos.data ?? []).filter((a) => a.exibivelInline);
  const abrirVisualizador = (anexo: AnexoChamado) => {
    const indice = exibiveis.findIndex((e) => e.id === anexo.id);
    if (indice >= 0) setVisualizando(indice);
  };

  if (!isOpen) return null;

  const isNovasFuncionalidades = ehNovaFuncionalidade(chamado?.categoria);

  // Loading state - mostra skeleton
  if (isLoading || !chamado) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl max-w-[1166px] w-full max-h-[90vh] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Loading */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-6 text-white">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <div className="flex-1 space-y-2">
                  <div className="h-6 bg-white/20 rounded animate-pulse w-48"></div>
                  <div className="h-4 bg-white/15 rounded animate-pulse w-72"></div>
                </div>
                <div className="w-20 h-8 bg-white/20 rounded-full animate-pulse"></div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors ml-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content Loading */}
          <div className="p-6">
            <div className="flex items-center justify-center min-h-96">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Carregando detalhes...
                </h3>
                <p className="text-gray-600">
                  Buscando informações completas do chamado
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-[1166px] w-full max-h-[90vh] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header do Modal */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1">
              <div className="flex-1">
                <h2 className="text-xl font-bold">Chamado #{chamado.id}</h2>
                <p className="text-blue-100 mt-1">{chamado.assunto}</p>
              </div>
              {/* Badge de Severidade ou Prioridade */}
              <div className="flex-shrink-0">
                {isNovasFuncionalidades ? (
                  <PrioridadeEditor
                    chamadoId={chamado.id}
                    prioridade={chamado.prioridade}
                    onSave={(id, prioridade) =>
                      updatePrioridade({ id, prioridade })
                    }
                    isPending={isPrioridadeUpdating}
                    variant="badge"
                  />
                ) : (
                  (() => {
                    const severidadeNum =
                      chamado.slaInfo?.severidadeAtendimento
                        ?.numeroSeveridade || chamado.severidade;

                    const getSeveridadeBadge = (num: number) => {
                      switch (num) {
                        case 1:
                          return {
                            label: "CRÍTICA",
                            class: "bg-red-100 text-red-800 border-red-300",
                            dotClass: "bg-red-500",
                          };
                        case 2:
                          return {
                            label: "ALTA",
                            class:
                              "bg-orange-100 text-orange-800 border-orange-300",
                            dotClass: "bg-orange-500",
                          };
                        case 3:
                          return {
                            label: "MÉDIA",
                            class:
                              "bg-yellow-100 text-yellow-800 border-yellow-300",
                            dotClass: "bg-yellow-500",
                          };
                        case 4:
                          return {
                            label: "BAIXA",
                            class: "bg-blue-100 text-blue-800 border-blue-300",
                            dotClass: "bg-blue-500",
                          };
                        default:
                          return {
                            label: "NORMAL",
                            class: "bg-gray-100 text-gray-800 border-gray-300",
                            dotClass: "bg-gray-500",
                          };
                      }
                    };

                    const severidadeBadge = getSeveridadeBadge(severidadeNum);

                    return (
                      <div
                        className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border shadow-sm ${severidadeBadge.class}`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full mr-2 ${severidadeBadge.dotClass}`}
                        ></div>
                        {severidadeBadge.label}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors ml-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Conteúdo do Modal */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Coluna Principal - Detalhes */}
            <div className="lg:col-span-2 space-y-6">
              {/* Informações Básicas */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3">
                  Informações do Chamado
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Status:</span>
                    {/* A etiqueta abre a mudança de status (equipe) ou as ações do cliente. */}
                    <span className="ml-2 inline-flex">
                      {escreve && equipe ? (
                        <EtiquetaStatusEquipe
                          idChamado={chamado.id}
                          idStatusAtual={chamado.status.id}
                          nomeStatusAtual={chamado.status.nome}
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                        />
                      ) : escreve ? (
                        <EtiquetaAcoesCliente
                          idChamado={chamado.id}
                          nomeStatusAtual={chamado.status.nome}
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                        />
                      ) : (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs">
                          {chamado.status.nome}
                        </span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">
                      Categoria:
                    </span>
                    <span className="ml-2">{chamado.categoria}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-600">
                      Prioridade:
                    </span>
                    <PrioridadeEditor
                      chamadoId={chamado.id}
                      prioridade={chamado.prioridade}
                      onSave={(id, prioridade) =>
                        updatePrioridade({ id, prioridade })
                      }
                      isPending={isPrioridadeUpdating}
                      variant="detail"
                    />
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">
                      Severidade:
                    </span>
                    <span className="ml-2">
                      {getSeveridadeLabel(chamado.severidade)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">
                      Responsável:
                    </span>
                    <span className="ml-2">{chamado.responsavel}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Cliente:</span>
                    <span className="ml-2">{chamado.cliente}</span>
                  </div>
                </div>
              </div>

              {/* Descrição */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Descrição</h3>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {chamado.descricao}
                </p>
              </div>

              {/* A linha do tempo da gestão, com a caixa de resposta e os anexos: dá para
                  responder, anexar e (equipe T4i) registrar nota interna sem sair do
                  quadro (pedido de 05/10/2026). */}
              {detalhe.data ? (
                <LinhaDoTempo
                  idChamado={chamado.id}
                  interacoes={detalhe.data.interacoes}
                  anexos={anexos.data ?? []}
                  aoVisualizar={abrirVisualizador}
                />
              ) : (
                <div className="flex justify-center py-6">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                </div>
              )}
            </div>

            {/* Sidebar - SLA e Datas */}
            <div className="space-y-6">
              {/* Informações de SLA */}
              {!isNovasFuncionalidades && chamado.slaInfo && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Status do SLA
                  </h3>
                  <div className="space-y-3">
                    {/* Indicador visual do SLA */}
                    <div
                      className={`p-3 rounded-xl border-2 ${
                        chamado.slaInfo.slaVencido
                          ? "bg-red-50 border-red-200 text-red-800"
                          : "bg-green-50 border-green-200 text-green-800"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          {chamado.slaInfo.slaVencido ? (
                            <AlertTriangle className="w-5 h-5 mr-2" />
                          ) : (
                            <CheckCircle className="w-5 h-5 mr-2" />
                          )}
                          <span className="font-semibold">
                            {chamado.slaInfo.statusSla}
                          </span>
                        </div>
                      </div>

                      {/* Barra de progresso do SLA */}
                      {chamado.slaInfo.dataLimiteConclusao &&
                        !chamado.dataConclusao && (
                          <div className="space-y-2">
                            <div className="flex justify-between text-xs font-medium">
                              <span>Progresso do SLA</span>
                              <span>
                                {(() => {
                                  const agora = new Date();
                                  const criacao = new Date(chamado.dataCriacao);
                                  const limite = new Date(
                                    chamado.slaInfo.dataLimiteConclusao,
                                  );
                                  const totalTime =
                                    limite.getTime() - criacao.getTime();
                                  const elapsedTime =
                                    agora.getTime() - criacao.getTime();
                                  const progress = Math.min(
                                    Math.max(
                                      (elapsedTime / totalTime) * 100,
                                      0,
                                    ),
                                    100,
                                  );
                                  return `${Math.round(progress)}%`;
                                })()}
                              </span>
                            </div>
                            <div className="w-full bg-white/60 rounded-full h-3 overflow-hidden">
                              <div
                                className={`h-3 rounded-full transition-all duration-300 ${(() => {
                                  const agora = new Date();
                                  const criacao = new Date(chamado.dataCriacao);
                                  const limite = new Date(
                                    chamado.slaInfo.dataLimiteConclusao,
                                  );
                                  const totalTime =
                                    limite.getTime() - criacao.getTime();
                                  const elapsedTime =
                                    agora.getTime() - criacao.getTime();
                                  const progress = Math.min(
                                    Math.max(
                                      (elapsedTime / totalTime) * 100,
                                      0,
                                    ),
                                    100,
                                  );

                                  if (chamado.slaInfo.slaVencido)
                                    return "bg-red-600";
                                  if (progress > 80) return "bg-red-500";
                                  if (progress > 60) return "bg-yellow-500";
                                  return "bg-green-500";
                                })()}`}
                                style={{
                                  width: `${(() => {
                                    const agora = new Date();
                                    const criacao = new Date(
                                      chamado.dataCriacao,
                                    );
                                    const limite = new Date(
                                      chamado.slaInfo.dataLimiteConclusao,
                                    );
                                    const totalTime =
                                      limite.getTime() - criacao.getTime();
                                    const elapsedTime =
                                      agora.getTime() - criacao.getTime();
                                    return Math.min(
                                      Math.max(
                                        (elapsedTime / totalTime) * 100,
                                        0,
                                      ),
                                      100,
                                    );
                                  })()}%`,
                                }}
                              />
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Compacto (05/10/2026): prazo e tempo restante lado a lado, e os tempos da
                        severidade numa linha — os anexos, logo abaixo, sobem para perto da linha do
                        tempo. */}
                    {chamado.slaInfo.dataLimiteConclusao && !chamado.dataConclusao && (
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="block text-xs font-medium text-gray-600">Prazo de conclusão</span>
                          <span className="text-gray-800">
                            {new Date(chamado.slaInfo.dataLimiteConclusao).toLocaleDateString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                            })}{" "}
                            às{" "}
                            {new Date(chamado.slaInfo.dataLimiteConclusao).toLocaleTimeString("pt-BR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        {!chamado.slaInfo.slaVencido && chamado.slaInfo.tempoRestanteConclusao && (
                          <div>
                            <span className="block text-xs font-medium text-gray-600">Tempo restante</span>
                            <span className="text-gray-800">
                              {formatTempoRestante(chamado.slaInfo.tempoRestanteConclusao)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-gray-600">
                      {chamado.slaInfo.severidadeAtendimento.severidade} · resposta{" "}
                      {chamado.slaInfo.tempoRespostaAplicado}h · conclusão{" "}
                      {chamado.slaInfo.tempoConclusaoAplicado}h
                    </p>
                  </div>
                </div>
              )}

              {/* Linha do tempo do SLA, datas e contato condensados (05/10/2026): grade de duas colunas,
                  data curta, sem repetir o que já está no card da SLA (prazo) nem a pausa duas vezes. */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <Activity className="w-5 h-5 mr-2" />
                  {!isNovasFuncionalidades && chamado.slaInfo ? "Linha do tempo do SLA" : "Datas"}
                </h3>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                  <Dado rotulo="Criação" valor={dataCurta(chamado.dataCriacao)} />
                  {chamado.dataConclusao && <Dado rotulo="Conclusão" valor={dataCurta(chamado.dataConclusao)} />}
                  {!isNovasFuncionalidades && chamado.slaInfo && (
                    <>
                      {chamado.slaInfo.dataEntradaFila && (
                        <Dado rotulo="Entrada na fila" valor={dataCurta(chamado.slaInfo.dataEntradaFila)} />
                      )}
                      {chamado.slaInfo.dataInicioSLA && (
                        <Dado
                          rotulo="Início do SLA"
                          valor={dataCurta(chamado.slaInfo.dataInicioSLA)}
                          nota={
                            new Date(chamado.slaInfo.dataInicioSLA).getTime() !==
                            new Date(chamado.dataCriacao).getTime()
                              ? "ativado via fila"
                              : undefined
                          }
                        />
                      )}
                      {chamado.slaInfo.dataFimSLA && (
                        <Dado rotulo="Fim do SLA" valor={dataCurta(chamado.slaInfo.dataFimSLA)} />
                      )}
                      <Dado
                        rotulo="Limite aplicado"
                        valor={`${chamado.slaInfo.tempoConclusaoAplicado}h`}
                        nota={chamado.slaInfo.tipoTempoConclusao?.toLowerCase()}
                      />
                      {chamado.slaInfo.tempoPausa && (
                        <Dado rotulo="Em pausa" valor={formatTempoRestante(chamado.slaInfo.tempoPausa)} />
                      )}
                      {chamado.slaInfo.statusFila && (
                        <Dado
                          rotulo="Situação na fila"
                          valor={chamado.slaInfo.statusFila.charAt(0) + chamado.slaInfo.statusFila.slice(1).toLowerCase()}
                        />
                      )}
                    </>
                  )}
                </dl>
              </div>

              {/* Os anexos, abaixo das duas caixas da SLA (05/10/2026). Status e ações ficam na
                  etiqueta de status. */}
              <Anexos
                idChamado={chamado.id}
                itens={anexos.data ?? []}
                carregando={anexos.isLoading}
                aoVisualizar={abrirVisualizador}
              />

              <div className="bg-gray-50 rounded-lg p-4 text-sm">
                <h3 className="font-semibold text-gray-800 mb-2">Contato</h3>
                <p className="text-gray-800">{chamado.nomeContato}</p>
                <p className="truncate text-gray-600" title={chamado.emailContato ?? undefined}>
                  {chamado.emailContato}
                </p>
                {chamado.localidade && <p className="text-gray-600">{chamado.localidade}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
      {visualizando !== null && exibiveis.length > 0 && (
        <VisualizadorAnexo
          idChamado={chamado.id}
          anexos={exibiveis}
          indiceInicial={visualizando}
          aoFechar={() => setVisualizando(null)}
        />
      )}
    </div>
  );
};

/** "05/10 09:56" — as datas da lateral condensada. */
const dataCurta = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

/** Um dado da grade: rótulo pequeno em cima, valor embaixo, nota opcional ao lado. */
const Dado = ({ rotulo, valor, nota }: { rotulo: string; valor: string; nota?: string }) => (
  <div className="min-w-0">
    <dt className="text-xs font-medium text-gray-500">{rotulo}</dt>
    <dd className="text-gray-800">
      {valor}
      {nota && <span className="ml-1 text-xs text-gray-500">({nota})</span>}
    </dd>
  </div>
);
